import React, { useEffect } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { AuthRoute } from './components/layout/AuthRoute';
import ErrorBoundary from './components/ErrorBoundary';
import * as Sentry from '@sentry/react';

// Core pages loaded immediately
import Index from './pages/Index';
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';

// Lazy-loaded pages with suspense
import {
  LazyDashboard,
  LazyOrganizations,
  LazyOrganizationDetails,
  LazyContacts,
  LazyContactDetails,
  LazyOpportunities,
  LazyOpportunityDetails,
  LazyProjects,
  LazyProjectDetails,
  LazyAnalytics,
  LazyCommunications,
  LazyEmailTemplates,
  LazyCampaignAnalytics,
  LazyDocuments,
  LazyDocumentDetails,
  LazyInvoices,
  LazyInvoiceDetails,
  LazyLandExpand,
  LazyAutomation,
  LazyRelationshipVisualizer,
  LazyHubSpotIntegration,
  LazyDataImport,
  LazyDataExport,
  LazyUserManagement,
  LazyContractGenerator,
  LazyCreateContact,
  LazyCreateOrganization,
  LazyCreateOpportunity,
  LazyCreateProject,
  LazyCreateTask,
  LazyCreateActivity,
  LazyCreateGift,
  LazyCreateInvoice,
  LazyEditContact,
  LazyEditOrganization,
  LazyEditOpportunity,
  LazyEditTask,
  LazyEditActivity,
  LazyEditGift,
  withSuspense,
  preloadCriticalRoutes,
  preloadRouteOnHover,
} from './utils/lazyRoutes';

// Other critical imports that need to be loaded immediately
import Calendar from './pages/Calendar';
import Tasks from './pages/Tasks';
import Activities from './pages/Activities';
import DocumentUpload from './pages/DocumentUpload';
import DocumentDetails from './pages/DocumentDetails';
import EditDocument from './pages/EditDocument';
import OrganizationHierarchyVisualizer from './pages/OrganizationHierarchyVisualizer';
import OrganizationDetails from './pages/OrganizationDetails';
import ContactDetails from './pages/ContactDetails';
import OpportunityDetails from './pages/OpportunityDetails';
import GrowthOpportunityView from './pages/GrowthOpportunityView';
import HolidayCampaigns from './pages/HolidayCampaigns';
import Communications from './pages/Communications';
import EmailTemplates from './pages/EmailTemplates';
import CampaignAnalytics from './pages/CampaignAnalytics';
import RelationshipVisualizer from './pages/RelationshipVisualizer';
import Gifts from './pages/Gifts';
import CustomActivityTypes from './pages/CustomActivityTypes';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/react-query';
import { TooltipProvider } from './components/ui/tooltip';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import ContractGenerator from './pages/ContractGenerator';
import EditContract from './pages/EditContract';
import Projects from './pages/Projects';
import CreateProject from './pages/CreateProject';
import ProjectDetails from './pages/ProjectDetails';
import CreateTask from './pages/CreateTask';
import EditTask from './pages/EditTask';
import CreateContact from './pages/CreateContact';
import EditContact from './pages/EditContact';
import CreateOrganization from './pages/CreateOrganization';
import EditOrganization from './pages/EditOrganization';
import CreateOpportunity from './pages/CreateOpportunity';
import EditOpportunity from './pages/EditOpportunity';
import CreateActivity from './pages/CreateActivity';
import EditActivity from './pages/EditActivity';
import CreateGift from './pages/CreateGift';
import EditGift from './pages/EditGift';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import Invoices from './pages/Invoices';
import CreateInvoice from './pages/CreateInvoice';
import InvoiceDetails from './pages/InvoiceDetails';
import HubSpotIntegration from './pages/HubSpotIntegration';
import HubSpotSetup from './pages/HubSpotSetup';
import HubSpotSync from './pages/HubSpotSync';
import HubSpotMapping from './pages/HubSpotMapping';
import HubSpotLogs from './pages/HubSpotLogs';
// import TestSentry from "./pages/TestSentry";
import './App.css';
import {
  SafeDashboard,
  SafeOrganizations,
  SafeContacts,
  SafeOpportunities,
  SafeAnalytics,
  SafeLandExpand,
  SafeDocuments,
  SafeGifts,
  SafeDataImport,
  SafeDataExport,
  SafeProjects,
  SafeUserManagement,
  SafeInvoices,
  SafeHubSpotIntegration,
  SafeDocs,
  SafeAutomation,
  SafeCommunicationHub,
  SafeFinancialDashboard,
  SafeSecurityDashboard,
  SafeIntegrationSettings,
} from './utils/safeComponents';

const router = createBrowserRouter([
  {
    path: '/',
    element: <Index />,
  },
  {
    element: <AuthRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: '/dashboard',
            element: <SafeDashboard />,
          },
          {
            path: '/calendar',
            element: <Calendar />,
          },
          {
            path: '/tasks',
            element: <Tasks />,
          },
          {
            path: '/tasks/create',
            element: <CreateTask />,
          },
          {
            path: '/tasks/:id/edit',
            element: <EditTask />,
          },
          {
            path: '/documents',
            element: <SafeDocuments />,
          },
          {
            path: '/documents/upload',
            element: <DocumentUpload />,
          },
          {
            path: '/documents/:id',
            element: <DocumentDetails />,
          },
          {
            path: '/documents/:id/edit',
            element: <EditDocument />,
          },
          {
            path: '/organizations',
            element: <SafeOrganizations />,
          },
          {
            path: '/organizations/create',
            element: <CreateOrganization />,
          },
          {
            path: '/organizations/:id',
            element: <OrganizationDetails />,
          },
          {
            path: '/organizations/:id/edit',
            element: <EditOrganization />,
          },
          {
            path: '/organization-hierarchy',
            element: <OrganizationHierarchyVisualizer />,
          },
          {
            path: '/organization-hierarchy/:organizationId',
            element: <OrganizationHierarchyVisualizer />,
          },
          {
            path: '/contacts',
            element: <SafeContacts />,
          },
          {
            path: '/contacts/create',
            element: <CreateContact />,
          },
          {
            path: '/contacts/:id',
            element: <ContactDetails />,
          },
          {
            path: '/contacts/:id/edit',
            element: <EditContact />,
          },
          {
            path: '/opportunities',
            element: <SafeOpportunities />,
          },
          {
            path: '/opportunities/create',
            element: <CreateOpportunity />,
          },
          {
            path: '/opportunities/:id',
            element: <OpportunityDetails />,
          },
          {
            path: '/opportunities/:id/edit',
            element: <EditOpportunity />,
          },
          {
            path: '/activities',
            element: <Activities />,
          },
          {
            path: '/activities/create',
            element: <CreateActivity />,
          },
          {
            path: '/activities/:id/edit',
            element: <EditActivity />,
          },
          {
            path: '/activities/types',
            element: <CustomActivityTypes />,
          },
          {
            path: '/analytics',
            element: <SafeAnalytics />,
          },
          {
            path: '/landexpand',
            element: <SafeLandExpand />,
          },
          {
            path: '/land-expand',
            element: <SafeLandExpand />,
          },
          {
            path: '/landexpand/:id',
            element: <GrowthOpportunityView />,
          },
          {
            path: '/land-expand/:id',
            element: <GrowthOpportunityView />,
          },
          {
            path: '/communications',
            element: <Communications />,
          },
          {
            path: '/communications/templates',
            element: <EmailTemplates />,
          },
          {
            path: '/communications/campaigns/analytics',
            element: <CampaignAnalytics />,
          },
          {
            path: '/communications/holiday',
            element: <HolidayCampaigns />,
          },
          {
            path: '/gifts',
            element: <SafeGifts />,
          },
          {
            path: '/gifts/create',
            element: <CreateGift />,
          },
          {
            path: '/gifts/:id/edit',
            element: <EditGift />,
          },
          {
            path: '/import',
            element: <SafeDataImport />,
          },
          {
            path: '/export',
            element: <SafeDataExport />,
          },
          {
            path: '/contracts/generate/:id',
            element: <ContractGenerator />,
          },
          {
            path: '/contracts/:id/edit',
            element: <EditContract />,
          },
          {
            path: '/projects',
            element: <SafeProjects />,
          },
          {
            path: '/projects/create',
            element: <CreateProject />,
          },
          {
            path: '/projects/:id',
            element: <ProjectDetails />,
          },
          {
            path: '/admin/users',
            element: <SafeUserManagement />,
          },
          {
            path: '/invoices',
            element: <SafeInvoices />,
          },
          {
            path: '/invoices/create',
            element: <CreateInvoice />,
          },
          {
            path: '/invoices/:id',
            element: <InvoiceDetails />,
          },
          {
            path: '/integrations/hubspot',
            element: <SafeHubSpotIntegration />,
          },
          {
            path: '/integrations/hubspot/setup',
            element: <HubSpotSetup />,
          },
          {
            path: '/integrations/hubspot/sync',
            element: <HubSpotSync />,
          },
          {
            path: '/integrations/hubspot/mapping',
            element: <HubSpotMapping />,
          },
          {
            path: '/integrations/hubspot/logs',
            element: <HubSpotLogs />,
          },
          {
            path: '/relationships/:type/:id',
            element: <RelationshipVisualizer />,
          },
          {
            path: '/settings',
            element: <Settings />,
          },
          {
            path: '/docs',
            element: <SafeDocs />,
          },
          {
            path: '/automation',
            element: <SafeAutomation />,
          },
          {
            path: '/communications/hub',
            element: <SafeCommunicationHub />,
          },
          {
            path: '/analytics/financial',
            element: <SafeFinancialDashboard />,
          },
          {
            path: '/admin/security',
            element: <SafeSecurityDashboard />,
          },
          {
            path: '/integrations',
            element: <SafeIntegrationSettings />,
          },
          // {
          //   path: "/test/sentry",
          //   element: <TestSentry />,
          // },
        ],
      },
    ],
  },
  {
    path: '/auth',
    element: <Auth />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
]);

function App() {
  // Preload critical routes on app initialization
  useEffect(() => {
    preloadCriticalRoutes();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Wrap the app with Sentry profiler for performance monitoring
const SentryApp = Sentry.withProfiler(App);
export default SentryApp;
